import { IconSearch } from '@maia/icons';
import {
  ClaimDto,
  CustomerDto,
  InsuranceDto,
  QuotationDto,
} from '@maia/shared';
import {
  Button,
  ClaimCard,
  CustomerCard,
  FormField,
  getErrors,
  Input,
  InsuranceCard,
  PageHeader,
  QuotationCard,
  Select,
  Typography,
} from '@maia/ui';
import { useForm } from '@tanstack/react-form';
import { createFileRoute } from '@tanstack/react-router';
import { match } from 'ts-pattern';
import * as v from 'valibot';
import {
  Category,
  useGetDataPerCategory,
} from '../../../../features/task/new/use-get-data-per-category';

export const Route = createFileRoute('/_authenticated/tasks/new/')({
  component: RouteComponent,
});

function RouteComponent() {
  const {
    result: { data, type },
    CATEGORIES,
    state: [_filters, setfilters],
  } = useGetDataPerCategory();

  const form = useForm({
    defaultValues: {
      category: undefined as Category | undefined,
      query: undefined as string | undefined,
    },
    onSubmit: ({ value }) => setfilters(value),
  });

  return (
    <div className="layout-simple">
      <PageHeader
        className="area-page-header"
        title="Koppel de taak"
        description="Link de taak met een item"
      />
      <div className="area-page-content content-grid">
        <div className="col-span-8 flex flex-col gap-y-24">
          <form
            className="flex gap-x-24 items-start"
            onSubmit={(e) => {
              e.preventDefault();
              form.handleSubmit();
            }}
          >
            <form.Field
              name="category"
              validators={{
                onChange: v.picklist(
                  CATEGORIES.map((c) => c.value),
                  'Selecteer een categorie',
                ),
              }}
            >
              {(field) => (
                <FormField
                  label=""
                  error={getErrors(field)}
                  inputSlot={
                    <Select
                      className="min-w-[190px]"
                      placeholder="Selecteer"
                      items={CATEGORIES}
                      onChange={(v) =>
                        field.handleChange(v.toString() as Category)
                      }
                      onBlur={field.handleBlur}
                      value={field.state.value}
                    />
                  }
                />
              )}
            </form.Field>
            <form.Field
              name="query"
              validators={{
                onChange: v.pipe(
                  v.string('Vul een zoekterm in'),
                  v.minLength(1, 'Vul een zoekterm in'),
                ),
              }}
            >
              {(field) => (
                <FormField
                  label=""
                  error={getErrors(field)}
                  inputSlot={
                    <Input
                      className="flex-grow"
                      icon={<IconSearch />}
                      placeholder="Zoeken"
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                    />
                  }
                />
              )}
            </form.Field>
            <form.Subscribe selector={(s) => s.canSubmit}>
              {(canSubmit) => (
                <Button
                  isDisabled={!canSubmit}
                  type="submit"
                  className="mt-12 h-48"
                  size="default"
                >
                  Zoeken
                </Button>
              )}
            </form.Subscribe>
          </form>
          {(type === 'customer' ||
            type === 'insurance' ||
            type === 'claim' ||
            type === 'quotation') &&
            !!data?.response?.length && (
              <ul className="flex flex-col gap-y-12">
                {data?.response.map((item) => (
                  <li key={item.guid}>
                    {match(type)
                      .with('customer', () => (
                        <CustomerCard
                          customer={item as CustomerDto}
                          to={{
                            to: '/tasks/new/customer',
                            search: { guid: item.guid },
                          }}
                        />
                      ))
                      .with('insurance', () => (
                        <InsuranceCard
                          insurance={item as InsuranceDto}
                          to={{
                            to: '/tasks/new/insurance',
                            search: { guid: item.guid },
                          }}
                        />
                      ))
                      .with('claim', () => (
                        <ClaimCard
                          claim={item as ClaimDto}
                          to={{
                            to: '/tasks/new/claim',
                            search: { guid: item.guid },
                          }}
                        />
                      ))
                      .with('quotation', () => (
                        <QuotationCard
                          quotation={item as QuotationDto}
                          to={{
                            to: '/tasks/new/quotation',
                            search: { guid: item.guid },
                          }}
                        />
                      ))
                      .otherwise(() => null)}
                  </li>
                ))}
              </ul>
            )}
          {data?.response?.length === 0 && (
            <div className="flex flex-col w-full justify-center items-center p-64 col-span-full border border-grayscale100 rounded-8">
              <img
                src={`/images/illustrations/empty-table.svg`}
                alt={'Geen resultaten gevonden'}
                className="object-contain w-[190px] h-[155px]"
              />
              <Typography
                variant="body-l"
                weight="medium"
                className="text-grayscale600"
              >
                Geen resultaten gevonden
              </Typography>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
